import { CancellablePromise } from "real-cancellable-promise"
import * as httpx from "httpx"

import {
	DocumentUploadResponse,
	DocumentExtract,
	AssetExtract,
	ParseStatusResponse,
	AssetApproveResponse,
	AssetRejectResponse,
	AssetConfigureRequest,
	AssetConfigureResponse,
} from "../brandguide_pb"

export {
	DocumentUploadResponse,
	DocumentExtract,
	AssetExtract,
	ParseStatusResponse,
	AssetApproveResponse,
	AssetRejectResponse,
	AssetConfigureRequest,
	AssetConfigureResponse,
}

export interface DocumentSearchRequest {
	brand_id: string
	limit: number
	offset: number
}

export interface DocumentSearchResponse {
	next: DocumentSearchRequest
	documents: DocumentExtract[]
	total_count: number
}

export interface AssetSearchRequest {
	document_id: string
	limit: number
	offset: number
	asset_type: string
	review_status: string
	search: string
}

export interface AssetSearchResponse {
	next: AssetSearchRequest
	assets: AssetExtract[]
	total_count: number
	pending_count: number
	approved_count: number
	rejected_count: number
}

export namespace zeros {
	export namespace documents {
		export function searchZero(r: Partial<DocumentSearchRequest> = {}): DocumentSearchRequest {
			return {
				brand_id: "",
				limit: 20,
				offset: 0,
				...r,
			}
		}

		export function extractZero(d: Partial<DocumentExtract> = {}): DocumentExtract {
			return {
				id: "",
				brand_id: "",
				account_id: "",
				profile_id: "",
				original_filename: "",
				file_path: "",
				file_type: "",
				file_size: 0n,
				file_md5: "",
				parse_status: "pending",
				parse_error: "",
				parsed_at: "",
				mistral_job_id: "",
				filter_job_id: "",
				created_at: "",
				updated_at: "",
				...d,
			}
		}
	}

	export namespace assets {
		export function searchZero(r: Partial<AssetSearchRequest> = {}): AssetSearchRequest {
			return {
				document_id: "",
				limit: 50,
				offset: 0,
				asset_type: "",
				review_status: "",
				search: "",
				...r,
			}
		}

		export function extractZero(a: Partial<AssetExtract> = {}): AssetExtract {
			return {
				id: "",
				document_id: "",
				brand_id: "",
				account_id: "",
				asset_type: "",
				asset_subtype: "",
				content_path: "",
				content_text: "",
				page_number: 0,
				position_x: 0,
				position_y: 0,
				width: 0,
				height: 0,
				confidence_score: 0,
				filter_score: 0,
				extracted_context: "",
				review_status: "pending",
				reviewed_at: "",
				reviewed_by: "",
				configured_as: "",
				description: "",
				clearspace_horizontal: 0,
				clearspace_vertical: 0,
				color: "",
				rule: "",
				sent_to_settings: false,
				sent_to_settings_at: "",
				sort_order: 0,
				created_at: "",
				updated_at: "",
				content_md5: "",
				mimetype: "",
				...a,
			}
		}

		export function configureRequestZero(r: Partial<AssetConfigureRequest> = {}): AssetConfigureRequest {
			return {
				configured_as: "",
				description: "",
				clearspace_horizontal: 0,
				clearspace_vertical: 0,
				color: "",
				rule: "",
				font_styles: ["regular", "bold", "italic", "light"],
				...r,
			}
		}
	}
}

// Document API
export namespace documents {
	export function upload(brandId: string, formData: FormData, bearer: httpx.option): CancellablePromise<DocumentUploadResponse> {
		return httpx.upload<DocumentUploadResponse>(
			`/brands/${brandId}/brandguide/v2/documents/upload`,
			formData,
			bearer,
		)
	}

	export function search(
		brandId: string,
		req: DocumentSearchRequest,
		bearer: httpx.option,
	): CancellablePromise<DocumentSearchResponse> {
		const params = {
			brand_id: brandId,
			limit: req.limit?.toString() || "20",
			offset: req.offset?.toString() || "0",
		}
		return httpx.get<DocumentSearchResponse>(
			`/brands/${brandId}/brandguide/v2/documents`,
			params,
			bearer,
		)
	}

	export function status(brandId: string, documentId: string, bearer: httpx.option): CancellablePromise<ParseStatusResponse> {
		return httpx.get<ParseStatusResponse>(
			`/brands/${brandId}/brandguide/v2/documents/${documentId}/status`,
			{ brand_id: brandId },
			bearer,
		)
	}

	export function destroy(brandId: string, documentId: string, bearer: httpx.option): CancellablePromise<void> {
		return httpx.destroy<void>(
			`/brands/${brandId}/brandguide/v2/documents/${documentId}`,
			{},
			bearer,
		)
	}
}

// Asset API
export namespace assets {
	export function search(
		brandId: string,
		documentId: string,
		req: AssetSearchRequest,
		bearer: httpx.option,
	): CancellablePromise<AssetSearchResponse> {
		const params = {
			brand_id: brandId,
			document_id: documentId,
			limit: req.limit?.toString() || "50",
			offset: req.offset?.toString() || "0",
			asset_type: req.asset_type || "",
			review_status: req.review_status || "",
		}
		return httpx.get<AssetSearchResponse>(
			`/brands/${brandId}/brandguide/v2/assets`,
			params,
			bearer,
		)
	}

	export function approve(
		brandId: string,
		assetId: string,
		documentId: string,
		bearer: httpx.option,
	): CancellablePromise<AssetApproveResponse> {
		return httpx.post<AssetApproveResponse>(
			`/brands/${brandId}/brandguide/v2/assets/${assetId}/approve?brand_id=${brandId}&document_id=${documentId}`,
			{},
			bearer,
		)
	}

	export function reject(
		brandId: string,
		assetId: string,
		documentId: string,
		bearer: httpx.option,
	): CancellablePromise<AssetRejectResponse> {
		return httpx.post<AssetRejectResponse>(
			`/brands/${brandId}/brandguide/v2/assets/${assetId}/reject?brand_id=${brandId}&document_id=${documentId}`,
			{},
			bearer,
		)
	}

	export function configure(
		brandId: string,
		assetId: string,
		documentId: string,
		config: AssetConfigureRequest,
		bearer: httpx.option,
	): CancellablePromise<AssetConfigureResponse> {
		return httpx.post<AssetConfigureResponse>(
			`/brands/${brandId}/brandguide/v2/assets/${assetId}/configure?brand_id=${brandId}&document_id=${documentId}`,
			config,
			bearer,
		)
	}

	export function destroy(brandId: string, assetId: string, documentId: string, bearer: httpx.option): CancellablePromise<void> {
		const params = {
			brand_id: brandId,
			document_id: documentId,
		}
		return httpx.destroy<void>(
			`/brands/${brandId}/brandguide/v2/assets/${assetId}`,
			params,
			bearer,
		)
	}

	export function bulkSendToSettings(
		brandId: string,
		documentId: string,
		assetIds: string[],
		bearer: httpx.option,
		options?: {
			send_to_training?: boolean
			is_on_brand?: boolean
			training_destination?: string
		},
	): CancellablePromise<void> {
		return httpx.post<void>(
			`/brands/${brandId}/brandguide/v2/uploadsettings?brand_id=${brandId}`,
			{
				document_id: documentId,
				asset_ids: assetIds,
				send_to_training: options?.send_to_training || false,
				is_on_brand: options?.is_on_brand || false,
				training_destination: options?.training_destination || "",
			},
			bearer,
		)
	}
}
